//@flow
import * as React from 'react';
import { Marker } from 'react-map-gl';
import {
    Columns,
    Divider,
    Box,
    Text,
    Icon,
    Flex,
    Position,
    Button
} from '@locus-taxy/neo-ui';
import { MasterOutlet } from 'common/models/internal/MasterOutlet';
import { CreateTerritory } from 'common/models/internal/CreateTerritoryPlan';
import theme from './TerritoryMarker.scss';
import { SelectTypes } from 'common/components/core/select/Select';
import { Select } from 'common/components/core/select';
import { SizeEnum } from 'common/enums/UIEnums';
import { useTranslation } from 'react-i18next';

type Props = {
    outlet: MasterOutlet,
    isSelected: boolean,
    territoryOptions: Array<{
        label: string,
        value: string
    }>,
    territoryName: string,
    planStatus?: string,
    selectedTerritory?: CreateTerritory,
    setSelectedOutletId: (id: string, outlet?: MasterOutlet) => void,
    isUnallocated: boolean,
    changeTerritory: (
        originalTerritoryId: string,
        newTerritoryId: string,
        outletId: string,
        unAllocatedOutlet?: MasterOutlet
    ) => void,
    territoryId: string,
    id: string,
    color: string,
    setHoveredOutletId: (outletId: string) => void
};

const TerritoryMarker = (props: Props): React.Node => {
    const { territoryOptions, changeTerritory } = props;
    const { t } = useTranslation('entityManagement');

    const [
        selectedTerritoryOption,
        setSelectedTerritoryOption
    ] = React.useState({
        value: props.selectedTerritory?.id || null,
        label: props.selectedTerritory?.name || null
    });

    const changeSelectedTerritory = React.useCallback(value => {
        setSelectedTerritoryOption(value);
    }, []);

    const changeMarkerTerritory = React.useCallback(() => {
        if (selectedTerritoryOption.value) {
            changeTerritory(
                props.territoryId,
                selectedTerritoryOption.value,
                props.id,
                props.isUnallocated ? props.outlet : null
            );
        }
    }, [
        changeTerritory,
        props.id,
        props.isUnallocated,
        props.outlet,
        props.territoryId,
        selectedTerritoryOption.value
    ]);

    const { setSelectedOutletId } = props;

    const onOutletMarkerClick = React.useCallback(() => {
        setSelectedOutletId(props.id, props.outlet);
    }, [props.id, props.outlet, setSelectedOutletId]);

    const deselectMarker = React.useCallback(() => {
        setSelectedTerritoryOption({
            value: props.selectedTerritory?.id || null,
            label: props.selectedTerritory?.name || null
        });
        setSelectedOutletId(null);
    }, [props.selectedTerritory, setSelectedOutletId]);

    const { setHoveredOutletId, id } = props;

    const onOutletMarkerHover = React.useCallback(() => {
        setHoveredOutletId(id);
    }, [id, setHoveredOutletId]);

    const onOutletMarkerHoverOut = React.useCallback(() => {
        setHoveredOutletId(null);
    }, [setHoveredOutletId]);

    return (
        <Marker
            longitude={props.outlet.locationInfo.location.latLng.lng}
            latitude={props.outlet.locationInfo.location.latLng.lat}
            isOpen={props.isSelected}
            className={
                props.isSelected ? theme.selectedMarkerContainer : undefined
            }
            offsetLeft={-10}
            offsetTop={-10}
        >
            <Icon
                icon="circle"
                size={Icon.Size.large}
                color={props.color}
                onMouseOver={onOutletMarkerHover}
                onMouseOut={onOutletMarkerHoverOut}
                onClick={onOutletMarkerClick}
                sx={{
                    zIndex: 2,
                    border: '1px solid white',
                    borderRadius: '14px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    ':hover': {
                        transform: ['scale(1.5)']
                    },
                    transform: [`scale(${props.isSelected ? 1.5 : 1})`],
                    transition: ['transform ease 200ms']
                }}
                isOpen={props.isSelected}
            />

            {props.isSelected ? (
                <Box bg="white" minWidth={300} boxShadow="normal">
                    <Box pr={8} bg={props.color}>
                        <Flex
                            height="40px"
                            py={1}
                            px={3}
                            sx={{
                                border: `1px solid ${props.color}`,
                                alignItems: 'center'
                            }}
                        >
                            <Icon
                                icon="location_on"
                                size="medium"
                                color="white"
                                pr={2}
                            />
                            <Text fontSize={2} fontWeight={600} color="white">
                                {props.outlet.name || props.outlet.code}
                            </Text>
                        </Flex>
                        <Position position="absolute" top={22} right={0}>
                            <Button
                                icon="cross"
                                size="medium"
                                onClick={deselectMarker}
                                appearance="icon"
                                color="white"
                            />
                        </Position>
                    </Box>
                    <Box p={2}>
                        <Columns space="small">
                            <Text fontSize={2} fontWeight={600}>
                                {t('createTerritory.address')}
                            </Text>

                            <Text fontSize={2}>
                                {props.outlet.locationInfo.location?.address?.getFullAddress() ||
                                    '-'}
                            </Text>
                        </Columns>
                    </Box>
                    <Divider color="grey.200" />
                    <Box p={2}>
                        <Columns space="small">
                            <Text fontSize={2} fontWeight={600}>
                                {t('createTerritory.territory')}
                            </Text>

                            <Text fontSize={2}>
                                {props.territoryName ||
                                    t('createTerritory.notAssigned')}
                            </Text>
                        </Columns>
                    </Box>
                    <Divider color="grey.200" />
                    {props.planStatus !== 'FROZEN' ? (
                        <Box p={2}>
                            <Columns space="small">
                                <Text fontSize={2} fontWeight={600}>
                                    {t('createTerritory.moveOutlet')}
                                </Text>
                                <Box width="110px">
                                    <Select
                                        noMarginBottom
                                        selectType={SelectTypes.Outline}
                                        selectSize={SizeEnum.Medium}
                                        options={territoryOptions}
                                        value={selectedTerritoryOption}
                                        onChange={changeSelectedTerritory}
                                        isSearchable
                                        isClearable={false}
                                    />
                                </Box>
                                <Button
                                    appearance="solid"
                                    palette="primary"
                                    size="medium"
                                    onClick={changeMarkerTerritory}
                                >
                                    {t('createTerritory.change')}
                                </Button>
                            </Columns>
                        </Box>
                    ) : null}
                </Box>
            ) : null}
        </Marker>
    );
};

export default React.memo(TerritoryMarker);
